import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import { GlobalContext } from '../../components/GlobalStateProvide/GlobalStateProvider';
import { patchRequestWithToken, postRequest } from '../../utils/ApiRequests';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Assuming this would be used in a real app with validation
const PlanForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { handleSnackBar } = useContext(GlobalContext);
  const [formData, setFormData] = useState({
    planName: '',
    planDescription: '',
    planType: 'Subscription',
    basePricePerJob: '',
    candidateDatabasePerHiringCredit: '',
    vacancyLimit: '',
    postJobLimit: '',
    downloadCvLimit: '',
    bulkCandidateCVsDownloadLimitInApplicant: '',
    contactApplicantLimit: '',
    planMetaData: [
      {
        planDiscount: '',
        planDuration: '',
        planAmount: '',
        planFinalAmount: '',
        planAmountWithIntroductoryOffer: '',
        additionalDiscount: '',
        promoCodeDiscount: [
          {
            promoCode: '',
            promoCodeDiscountPercentage: 0,
          },
        ],
      },
    ],
    hiringCredit: '',
    applicantLimitPerJob: '',
    additionalApplicantCost: '',
    jobExpiryDays: '',
    expiryDays: '',
    additionalFeatures: [],
    hcValue: '',
    planStatus: 'active',
    planImage: '',
    planCode: '',
    planFor: 'Employers',
    planChooseIf: '',
    gstPercentage: {
      cgst: '',
      sgst: '',
      igst: '',
    },
    isVisibleToEveryOne: true,
    isBetaVersion: false,
    employerIds: [],
  });

  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [validationAlert, setValidationAlert] = useState(null);

  useEffect(() => {
    if (location.state) {
      console.log("state is  :", location.state)


      const filteredData = {};

      // Only copy fields that exist in formData
      Object.keys(formData).forEach(key => {
        if (location.state[key] !== undefined) {
          filteredData[key] = location.state[key];
        } else {
          // Keep the default values for missing fields
          filteredData[key] = formData[key];
        }
      });

      // Add the planId from _id
      filteredData.planId = location.state._id || null;

      setFormData(filteredData);

      setIsEditMode(true);
    }
  }, [location.state]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Clear validation alert when changing tabs manually
    setValidationAlert(null);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleGstChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      gstPercentage: {
        ...prev.gstPercentage,
        [name]: Number(value),
      },
    }));
  };

  const handleMetaDataChange = (index, field, value) => {
    const updatedMetaData = [...formData.planMetaData];
    updatedMetaData[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      planMetaData: updatedMetaData,
    }));
  };

  const addPromoCode = (metaDataIndex) => {
    const updatedMetaData = [...formData.planMetaData];
    updatedMetaData[metaDataIndex].promoCodeDiscount.push({
      promoCode: '',
      promoCodeDiscountPercentage: 0,
    });
    setFormData((prev) => ({
      ...prev,
      planMetaData: updatedMetaData,
    }));
  };

  const handlePromoCodeChange = (metaDataIndex, promoIndex, field, value) => {
    const updatedMetaData = [...formData.planMetaData];
    updatedMetaData[metaDataIndex].promoCodeDiscount[promoIndex][field] = value;
    setFormData((prev) => ({
      ...prev,
      planMetaData: updatedMetaData,
    }));
  };

  const removePromoCode = (metaDataIndex, promoIndex) => {
    const updatedMetaData = [...formData.planMetaData];
    updatedMetaData[metaDataIndex].promoCodeDiscount.splice(promoIndex, 1);
    setFormData((prev) => ({
      ...prev,
      planMetaData: updatedMetaData,
    }));
  };

  const addMetaData = () => {
    setFormData((prev) => ({
      ...prev,
      planMetaData: [
        ...prev.planMetaData,
        {
          planDiscount: 0,
          planDuration: '',
          planAmount: 0,
          planFinalAmount: 0,
          planAmountWithIntroductoryOffer: 0,
          additionalDiscount: 0,
          promoCodeDiscount: [],
        },
      ],
    }));
  };

  const removeMetaData = (index) => {
    const updatedMetaData = [...formData.planMetaData];
    updatedMetaData.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      planMetaData: updatedMetaData,
    }));
  };

  const addAdditionalFeature = () => {
    setFormData((prev) => ({
      ...prev,
      additionalFeatures: [...prev.additionalFeatures, { text: '', image: '' }],
    }));
  };

  const handleAdditionalFeatureChange = (index, field, value) => {
    const updatedFeatures = [...formData.additionalFeatures];
    updatedFeatures[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      additionalFeatures: updatedFeatures,
    }));
  };

  const removeAdditionalFeature = (index) => {
    const updatedFeatures = [...formData.additionalFeatures];
    updatedFeatures.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      additionalFeatures: updatedFeatures,
    }));
  };

  // Map error fields to their respective tabs
  const fieldToTabMap = {
    // Tab 0 - Basic Information
    planName: 0,
    planDescription: 0,
    planCode: 0,
    planType: 0,
    planFor: 0,
    planStatus: 0,
    planChooseIf: 0,
    planImage: 0,

    // Tab 1 - Pricing & Limits
    basePricePerJob: 1,
    candidateDatabasePerHiringCredit: 1,
    vacancyLimit: 1,
    hiringCredit: 1,
    postJobLimit: 1,
    downloadCvLimit: 1,
    bulkCandidateCVsDownloadLimitInApplicant: 1,
    contactApplicantLimit: 1,
    applicantLimitPerJob: 1,
    additionalApplicantCost: 1,
    hcValue: 1,
    jobExpiryDays: 1,
    expiryDays: 1,
    planMetaData_0_planDuration: 1,
    planMetaData_0_planAmount: 1,
    planMetaData_0_planDiscount: 1,
    planMetaData_0_planFinalAmount: 1,
    planMetaData_0_planAmountWithIntroductoryOffer: 1,
    planMetaData_0_additionalDiscount: 1,

    // Tab 2 - Features & Metadata
    additionalFeatures: 2,

    // Tab 3 - Tax & Visibility
    gstPercentage_sgst: 3,
    gstPercentage_igst: 3,
    gstPercentage_cgst: 3,
    isVisibleToEveryOne: 3,
    isBetaVersion: 3,
    employerIds: 3,
  };

  const validateForm = () => {
    const newErrors = {};


    // Basic Information Tab Validation
    if (!formData.planName.trim()) {
      newErrors.planName = 'required';
    }

    if (!formData.planDescription.trim()) {
      newErrors.planDescription = 'required';
    }

    if (!formData.planCode.trim()) {
      newErrors.planCode = 'required';
    }

    if (!formData.planType.trim()) {
      newErrors.planType = 'required';
    }
    if (!formData.planFor.trim()) {
      newErrors.planFor = 'required';
    }

    if (!formData.planImage) {
      newErrors.planImage = 'required';
    }
    if (formData.basePricePerJob === '' || formData.basePricePerJob === null || formData.basePricePerJob === undefined) {
      newErrors.basePricePerJob = 'Required';
    } else if (Number(formData.basePricePerJob) < 0) {
      newErrors.basePricePerJob = 'Cannot be negative';
    } else if (Number(formData.basePricePerJob) === 0) {
      newErrors.basePricePerJob = 'Must be greater than 0';
    }

    if (formData.candidateDatabasePerHiringCredit === '' || formData.candidateDatabasePerHiringCredit === null || formData.candidateDatabasePerHiringCredit === undefined) {
      newErrors.candidateDatabasePerHiringCredit = 'Required';
    } else if (Number(formData.candidateDatabasePerHiringCredit) < 0) {
      newErrors.candidateDatabasePerHiringCredit = 'Cannot be negative';
    } else if (Number(formData.candidateDatabasePerHiringCredit) === 0) {
      newErrors.candidateDatabasePerHiringCredit = 'Must be greater than 0';
    }

    if (formData.vacancyLimit === '' || formData.vacancyLimit === null || formData.vacancyLimit === undefined) {
      newErrors.vacancyLimit = 'Required';
    } else if (Number(formData.vacancyLimit) < 0) {
      newErrors.vacancyLimit = 'Cannot be negative';
    } else if (Number(formData.vacancyLimit) === 0) {
      newErrors.vacancyLimit = 'Must be greater than 0';
    }

    if (formData.hiringCredit === '' || formData.hiringCredit === null || formData.hiringCredit === undefined) {
      newErrors.hiringCredit = 'Required';
    } else if (Number(formData.hiringCredit) < 0) {
      newErrors.hiringCredit = 'Cannot be negative';
    } else if (Number(formData.hiringCredit) === 0) {
      newErrors.hiringCredit = 'Must be greater than 0';
    }

    // Repeat for each numeric field:
    if (formData.postJobLimit === '' || formData.postJobLimit === null || formData.postJobLimit === undefined) {
      newErrors.postJobLimit = 'Required';
    } else if (Number(formData.postJobLimit) < 0) {
      newErrors.postJobLimit = 'Cannot be negative';
    } else if (Number(formData.postJobLimit) === 0) {
      newErrors.postJobLimit = 'Must be greater than 0';
    }


    if (formData.downloadCvLimit === '' || formData.downloadCvLimit === null || formData.downloadCvLimit === undefined) {
      newErrors.downloadCvLimit = 'required';
    }
    else if (Number(formData.downloadCvLimit) < 0) {
      newErrors.downloadCvLimit = 'Cannot be negative';
    } else if (Number(formData.downloadCvLimit) === 0) {
      newErrors.downloadCvLimit = 'Must be greater than 0';
    }

    if (formData.bulkCandidateCVsDownloadLimitInApplicant === '' || formData.bulkCandidateCVsDownloadLimitInApplicant === null || formData.bulkCandidateCVsDownloadLimitInApplicant === undefined) {
      newErrors.bulkCandidateCVsDownloadLimitInApplicant = 'required';
    }
    else if (Number(formData.bulkCandidateCVsDownloadLimitInApplicant) < 0) {
      newErrors.bulkCandidateCVsDownloadLimitInApplicant = 'Cannot be negative';
    } else if (Number(formData.bulkCandidateCVsDownloadLimitInApplicant) === 0) {
      newErrors.bulkCandidateCVsDownloadLimitInApplicant = 'Must be greater than 0';
    }


    if (!formData.planChooseIf) {
      newErrors.planChooseIf = 'required';
    }

    if (!formData.contactApplicantLimit) {
      newErrors.contactApplicantLimit = 'required';
    }
    else if (Number(formData.contactApplicantLimit) < 0) {
      newErrors.contactApplicantLimit = 'Cannot be negative';
    }
    else if (Number(formData.contactApplicantLimit) === 0) {
      newErrors.contactApplicantLimit = 'Must be greater than 0';
    }

    if (!formData.applicantLimitPerJob) {
      newErrors.applicantLimitPerJob = 'Required';
    } else if (Number(formData.applicantLimitPerJob) < 0) {
      newErrors.applicantLimitPerJob = 'Cannot be negative';
    } else if (Number(formData.applicantLimitPerJob) === 0) {
      newErrors.applicantLimitPerJob = 'Must be greater than 0';
    }


    if (!formData.additionalApplicantCost) {
      newErrors.additionalApplicantCost = 'required';
    }
    else if (Number(formData.additionalApplicantCost) < 0) {
      newErrors.additionalApplicantCost = 'Cannot be negative';
    } else if (Number(formData.additionalApplicantCost) === 0) {
      newErrors.additionalApplicantCost = 'Must be greater than 0';
    }

    if (!formData.hcValue) {
      newErrors.hcValue = 'required';
    }
    if (formData.jobExpiryDays === '' || formData.jobExpiryDays === null || formData.jobExpiryDays === undefined) {
      newErrors.jobExpiryDays = 'Required';
    } else if (Number(formData.jobExpiryDays) < 0) {
      newErrors.jobExpiryDays = 'Cannot be negative';
    }

    if (formData.expiryDays === '' || formData.expiryDays === null || formData.expiryDays === undefined) {
      newErrors.expiryDays = 'Required';
    } else if (Number(formData.expiryDays) < 0) {
      newErrors.expiryDays = 'Cannot be negative';
    }

    if (!formData.additionalFeatures) {
      newErrors.additionalFeatures = 'required';
    }

    formData.planMetaData.forEach((meta, index) => {
      const numericFields = [
        { key: 'planDuration', label: 'Plan duration' },
        { key: 'planAmount', label: 'Plan amount' },
        { key: 'planDiscount', label: 'Plan discount' },
        { key: 'planFinalAmount', label: 'Final amount' },
        { key: 'planAmountWithIntroductoryOffer', label: 'Amount with introductory offer' },
        { key: 'additionalDiscount', label: 'Additional discount' },
      ];

      numericFields.forEach(({ key, label }) => {
        const value = meta[key];
        if (value === '' || value === null || value === undefined) {
          newErrors[`planMetaData_${index}_${key}`] = `${label} is required`;
        } else if (Number(value) < 0) {
          newErrors[`planMetaData_${index}_${key}`] = `${label} cannot be negative`;
        }
      });

      // Validate promoCodeDiscount
      if (Array.isArray(meta.promoCodeDiscount)) {
        meta.promoCodeDiscount.forEach((promo, promoIndex) => {
          const percentage = Number(promo.promoCodeDiscountPercentage);
          const code = promo.promoCode;

          if (percentage > 0 && (!code || code.trim() === '')) {
            newErrors[`planMetaData_${index}_promoCodeDiscount_${promoIndex}_promoCode`] = 'Promo code is required when discount percentage is greater than 0';
          }

          if (percentage < 0) {
            newErrors[`planMetaData_${index}_promoCodeDiscount_${promoIndex}_promoCodeDiscountPercentage`] = 'why Negative?';
          }
        });
      }
    });

    // Additional Features Tab Validation
    formData.additionalFeatures.forEach((feature, index) => {
      if (!feature.text.trim()) {
        newErrors[`additionalFeatures_${index}_text`] = 'Feature text is required';
      }
    });

    let isValid = true;
    // Validate additionalFeatures only if there are any
    if (formData.additionalFeatures.length > 0) {
      const featureErrors = [];

      formData.additionalFeatures.forEach((feature, index) => {
        const featureError = {};

        if (!feature.text || feature.text.trim() === '') {
          featureError.text = 'Feature text is required';
          isValid = false;
        }

        if (!feature.image || feature.image.trim() === '') {
          featureError.image = 'Feature image URL is required';
          isValid = false;
        }

        // Only add to errors array if there are actual errors
        if (Object.keys(featureError).length > 0) {
          featureErrors[index] = featureError;
        }
      });

      if (featureErrors.length > 0) {
        errors.additionalFeatures = featureErrors;
      }
    }

    // Tax & Visibility Tab Validation
    if (formData.gstPercentage.cgst === '' || formData.gstPercentage.cgst === null || formData.gstPercentage.cgst === undefined || Number(formData.gstPercentage.cgst) < 0) {
      newErrors.gstPercentage_cgst = 'CGST must be a valid non-negative number';
    }

    if (formData.gstPercentage.sgst === '' || formData.gstPercentage.sgst === null || formData.gstPercentage.sgst === undefined || Number(formData.gstPercentage.sgst) < 0) {
      newErrors.gstPercentage_sgst = 'SGST must be a valid non-negative number';
    }

    if (formData.gstPercentage.igst === '' || formData.gstPercentage.igst === null || formData.gstPercentage.igst === undefined || Number(formData.gstPercentage.igst) < 0) {
      newErrors.gstPercentage_igst = 'IGST must be a valid non-negative number';
    }

    setErrors(newErrors);
    return newErrors;
  };

  const findFirstErrorTab = (errors) => {
    // Get all error keys
    const errorKeys = Object.keys(errors);
    // console.log("errorKeys : ", errorKeys)

    if (errorKeys.length === 0) return 0;

    // Find tab index for each error and get the minimum tab index
    const tabIndices = errorKeys.map(key => {
      // Handle special case for nested fields
      if (key.startsWith('planMetaData_')) return 1;
      if (key.startsWith('additionalFeatures_')) return 2;

      // Default to the mapping or tab 0 if not found
      return fieldToTabMap[key] !== undefined ? fieldToTabMap[key] : 0;
    });

    return Math.min(...tabIndices);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationAlert(null);

    const newErrors = validateForm(); // modify this to return newErrors object
    // return;
    setErrors(newErrors);

    // if (validateForm()) {
    if (Object.keys(newErrors).length === 0) {
      try {
        let res = isEditMode ? await patchRequestWithToken('admin/plan', formData) : await postRequest('admin/plan', formData);
        if (res.data.status === 'Success') {
          setFormSubmitted(false);

          handleSnackBar(true, res.data.msg, "success");
          setTimeout(() => {
            navigate('/plans ');
          }, 1000);

        }
        else {
          setFormSubmitted(false);
          handleSnackBar(true, res.data.msg, "error")
        }
      } catch (err) {
        console.log(err);
        setFormSubmitted(false);
        handleSnackBar(true, err?.response?.data?.msg, "error")
      }
    } else {
      // Find the first tab with errors and switch to it
      const errorTab = findFirstErrorTab(newErrors);
      console.log("errortabvalue : ", errorTab)
      setActiveTab(errorTab);

      // Show a validation alert
      setValidationAlert(`Please correct the errors in the ${getTabName(errorTab)} tab before submitting.`);

      // Scroll to top to see the alert
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getTabName = (tabIndex) => {
    const tabNames = ['Basic Information', 'Pricing & Limits', 'Features', 'Tax & Visibility'];
    return tabNames[tabIndex] || '';
  };
  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
      <Paper elevation={3} sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Basic Information" />
            <Tab label="Pricing & Limits" />
            <Tab label="Features" />
            <Tab label="Tax & Visibility" />
          </Tabs>
        </Box>

        {/* Basic Information Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Plan Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Plan Name"
                  name="planName"
                  value={formData.planName}
                  onChange={handleChange}
                  error={!!errors.planName}
                  helperText={errors.planName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Plan Code"
                  name="planCode"
                  value={formData.planCode}
                  onChange={handleChange}
                  error={!!errors.planCode}
                  helperText={errors.planCode}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Plan Description"
                  name="planDescription"
                  value={formData.planDescription}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  error={!!errors.planDescription}
                  helperText={errors.planDescription}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormHelperText>Select the plan type</FormHelperText>
                  <Select
                    // label="Plan Type"
                    name="planType"
                    value={formData.planType}
                    onChange={handleChange}

                  >
                    <MenuItem value="Subscription">Subscription</MenuItem>
                    <MenuItem value="Pay Per Job">Pay Per Job</MenuItem>
                  </Select>
                  {errors.planType && (
                    <FormHelperText error>{errors.planType}</FormHelperText>
                  )}

                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <FormHelperText>Who is this plan for?</FormHelperText>
                  <Select
                    // label="Plan For"
                    name="planFor"
                    value={formData.planFor}
                    onChange={handleChange}
                  >
                    <MenuItem value="Employers">Employers</MenuItem>
                    <MenuItem value="Job Seekers">Job Seekers</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Plan Status"
                  name="planStatus"
                  select
                  value={formData.planStatus}
                  onChange={handleChange}
                >
                  <MenuItem value="active">Active</MenuItem>
                 
                </TextField>
              </Grid> */}
              <Grid item xs={12} md={6} display="flex" alignItems="center">
                <TextField
                  fullWidth
                  label="Plan Choose If"
                  name="planChooseIf"
                  value={formData.planChooseIf}
                  onChange={handleChange}
                  placeholder="When should someone choose this plan?"
                  error={!!errors.planChooseIf}
                  helperText={errors.planChooseIf}
                />
                <Tooltip

                  title={
                    <Box>
                      <Typography variant="body2">Consider the following when filling this:</Typography>
                      <ul style={{ paddingLeft: 16, margin: '4px 0' }}>
                        <li><Typography variant="body2">Perfect for one time hiring</Typography></li>
                        <li><Typography variant="body2">Perfect for upto 10 job posts</Typography></li>
                        <li><Typography variant="body2">Perfect for upto 30 job posts</Typography></li>
                      </ul>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <InfoOutlinedIcon color="action" fontSize="small" style={{ marginRight: 8, cursor: 'pointer' }} />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Plan Image URL"
                  name="planImage"
                  value={formData.planImage}
                  onChange={handleChange}
                  error={!!errors.planImage}
                  helperText={errors.planImage}
                  InputProps={{
                    endAdornment: formData.planImage && (
                      <InputAdornment position="end">
                        <IconButton>
                          <ImageIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {formData.planImage && (
                  <Box mt={2} sx={{ maxWidth: 200 }}>
                    <img
                      src={formData.planImage}
                      alt="Plan Preview"
                      style={{ width: '100%', borderRadius: 8 }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Pricing & Limits Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pricing & Usage Limits
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Base Price Per Job"
                  name="basePricePerJob"
                  type="number"
                  error={!!errors.basePricePerJob}
                  helperText={errors.basePricePerJob}
                  value={formData.basePricePerJob}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}

                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Candidate database credits"
                  name="candidateDatabasePerHiringCredit"
                  type="number"
                  error={!!errors.candidateDatabasePerHiringCredit}
                  helperText={errors.candidateDatabasePerHiringCredit}
                  value={formData.candidateDatabasePerHiringCredit}
                  onChange={handleChange}


                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vacancy Cap"
                  name="vacancyLimit"
                  type="number"
                  error={!!errors.vacancyLimit}
                  helperText={errors.vacancyLimit}
                  value={formData.vacancyLimit}
                  onChange={handleChange}


                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hiring Credit"
                  name="hiringCredit"
                  type="number"
                  error={!!errors.hiringCredit}
                  helperText={errors.hiringCredit}
                  value={formData.hiringCredit}
                  onChange={handleChange}

                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Post Job Limit"
                  name="postJobLimit"
                  type="number"
                  error={!!errors.postJobLimit}
                  helperText={errors.postJobLimit}
                  value={formData.postJobLimit}
                  onChange={handleChange}

                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Download CV Limit"
                  name="downloadCvLimit"
                  type="number"
                  error={!!errors.downloadCvLimit}
                  helperText={errors.downloadCvLimit}
                  value={formData.downloadCvLimit}
                  onChange={handleChange}

                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Applicant CVs Bulk Download Price"
                  name="bulkCandidateCVsDownloadLimitInApplicant"
                  type="number"
                  error={!!errors.bulkCandidateCVsDownloadLimitInApplicant}
                  helperText={errors.bulkCandidateCVsDownloadLimitInApplicant}
                  value={formData.bulkCandidateCVsDownloadLimitInApplicant}
                  onChange={handleChange}

                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Applicant Limit"
                  name="contactApplicantLimit"
                  type="number"
                  error={!!errors.contactApplicantLimit}
                  helperText={errors.contactApplicantLimit}
                  value={formData.contactApplicantLimit}
                  onChange={handleChange}

                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Applicant Limit Per Job"
                  name="applicantLimitPerJob"
                  type="number"
                  error={!!errors.applicantLimitPerJob}
                  helperText={errors.applicantLimitPerJob}
                  value={formData.applicantLimitPerJob}
                  onChange={handleChange}

                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Additional Applicant Cost"
                  name="additionalApplicantCost"
                  type="number"
                  error={!!errors.additionalApplicantCost}
                  helperText={errors.additionalApplicantCost}
                  value={formData.additionalApplicantCost}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}

                />
              </Grid>
              <Grid item xs={12} md={6} display="flex" alignItems="center">
                <TextField
                  fullWidth
                  label="HC Value"
                  name="hcValue"
                  error={!!errors.hcValue}
                  helperText={errors.hcValue}
                  value={formData.hcValue}
                  onChange={handleChange}
                  placeholder="1 Hiring credit = 1 INR"
                />
                <Tooltip

                  title={
                    <Box>
                      <Typography variant="body2">Consider the following when filling this:</Typography>
                      <ul style={{ paddingLeft: 16, margin: '4px 0' }}>
                        <li><Typography variant="body2">1 Hiring credit = 1 INR</Typography></li>

                      </ul>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <InfoOutlinedIcon color="action" fontSize="small" style={{ marginRight: 8, cursor: 'pointer' }} />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Job Expiry Days"
                  name="jobExpiryDays"
                  type="number"
                  error={!!errors.jobExpiryDays}
                  helperText={errors.jobExpiryDays}
                  value={formData.jobExpiryDays}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                  }}

                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Plan Expiry Days"
                  name="expiryDays"
                  type="number"
                  error={!!errors.expiryDays}
                  helperText={errors.expiryDays}
                  value={formData.expiryDays}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                  }}

                />
              </Grid>
            </Grid>

            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Plan Meta Data
                {/* <IconButton color="primary" onClick={addMetaData} size="small" sx={{ ml: 1 }}>
                  <AddIcon />
                </IconButton> */}
              </Typography>

              {formData.planMetaData.map((meta, metaIndex) => (
                <Card key={metaIndex} sx={{ mb: 3, p: 2, position: 'relative' }}>
                  {/* <IconButton
                    color="error"
                    size="small"
                    onClick={() => removeMetaData(metaIndex)}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                    disabled={formData.planMetaData.length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton> */}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Plan Duration"
                        value={meta.planDuration}
                        onChange={(e) => handleMetaDataChange(metaIndex, 'planDuration', e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">months</InputAdornment>,
                        }}
                        error={!!errors.planMetaData_0_planDuration}
                        helperText={errors.planMetaData_0_planDuration}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Plan Amount"
                        type="number"
                        value={meta.planAmount}
                        onChange={(e) => handleMetaDataChange(metaIndex, 'planAmount', Number(e.target.value))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        error={!!errors.planMetaData_0_planAmount}
                        helperText={errors.planMetaData_0_planAmount}

                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Plan Discount"
                        type="number"
                        value={meta.planDiscount}
                        onChange={(e) => handleMetaDataChange(metaIndex, 'planDiscount', Number(e.target.value))}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        error={!!errors.planMetaData_0_planDiscount}
                        helperText={errors.planMetaData_0_planDiscount}

                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Final Amount"
                        type="number"
                        value={meta.planFinalAmount}
                        onChange={(e) => handleMetaDataChange(metaIndex, 'planFinalAmount', Number(e.target.value))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        error={!!errors.planMetaData_0_planFinalAmount}
                        helperText={errors.planMetaData_0_planFinalAmount}

                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Amount With Intro Offer"
                        type="number"
                        value={meta.planAmountWithIntroductoryOffer}
                        onChange={(e) => handleMetaDataChange(metaIndex, 'planAmountWithIntroductoryOffer', Number(e.target.value))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        error={!!errors.planMetaData_0_planAmountWithIntroductoryOffer}
                        helperText={errors.planMetaData_0_planAmountWithIntroductoryOffer}

                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Additional Discount"
                        type="number"
                        value={meta.additionalDiscount}
                        onChange={(e) => handleMetaDataChange(metaIndex, 'additionalDiscount', Number(e.target.value))}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        error={!!errors.planMetaData_0_additionalDiscount}
                        helperText={errors.planMetaData_0_additionalDiscount}

                      />
                    </Grid>
                  </Grid>

                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Promo Codes
                      {/* <IconButton color="primary" onClick={() => addPromoCode(metaIndex)} size="small" sx={{ ml: 1 }}>
                        <AddIcon />
                      </IconButton> */}
                    </Typography>

                    {meta.promoCodeDiscount.map((promo, promoIndex) => (
                      <Stack key={promoIndex} direction="row" spacing={2} mb={1} alignItems="center">
                        <TextField
                          label="Promo Code"
                          value={promo.promoCode}
                          onChange={(e) => handlePromoCodeChange(metaIndex, promoIndex, 'promoCode', e.target.value)}
                          sx={{ flexGrow: 1 }}
                          error={!!errors.planMetaData_0_promoCodeDiscount_0_promoCode}
                          helperText={errors.planMetaData_0_promoCodeDiscount_0_promoCode}
                        />
                        <TextField
                          label="Discount %"
                          type="number"
                          value={promo.promoCodeDiscountPercentage}
                          onChange={(e) => handlePromoCodeChange(metaIndex, promoIndex, 'promoCodeDiscountPercentage', Number(e.target.value))}
                          sx={{ width: 120 }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          error={!!errors.planMetaData_0_promoCodeDiscount_0_promoCodeDiscountPercentage}
                          helperText={errors.planMetaData_0_promoCodeDiscount_0_promoCodeDiscountPercentage}

                        />
                        {/* <IconButton color="error" onClick={() => removePromoCode(metaIndex, promoIndex)}>
                          <DeleteIcon />
                        </IconButton> */}
                      </Stack>
                    ))}
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* Features & Metadata Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Additional Features
              <IconButton color="primary" onClick={addAdditionalFeature} size="small" sx={{ ml: 1 }}>
                <AddIcon />
              </IconButton>
            </Typography>

            {formData.additionalFeatures.map((feature, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    fullWidth
                    label="Feature Text"
                    value={feature.text}
                    onChange={(e) => handleAdditionalFeatureChange(index, 'text', e.target.value)}
                    error={errors.additionalFeatures && errors.additionalFeatures[index]?.text}
                    helperText={errors.additionalFeatures && errors.additionalFeatures[index]?.text}
                    required={formData.additionalFeatures.length > 0}
                  />
                  <TextField
                    fullWidth
                    label="Feature Image URL"
                    value={feature.image}
                    onChange={(e) => handleAdditionalFeatureChange(index, 'image', e.target.value)}
                    error={errors.additionalFeatures && errors.additionalFeatures[index]?.image}
                    helperText={errors.additionalFeatures && errors.additionalFeatures[index]?.image}
                    required={formData.additionalFeatures.length > 0}
                  />
                  <IconButton color="error" onClick={() => removeAdditionalFeature(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
                {feature.image && (
                  <Box mt={1} sx={{ maxWidth: 100 }}>
                    <img
                      src={feature.image}
                      alt="Feature Preview"
                      style={{ width: '100%', borderRadius: 4 }}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Tax & Visibility Tab */}
        {activeTab === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tax Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="CGST"
                  name="cgst"
                  type="number"
                  value={formData.gstPercentage.cgst}
                  onChange={handleGstChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  error={!!errors.gstPercentage_cgst}
                  helperText={errors.gstPercentage_cgst}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="SGST"
                  name="sgst"
                  type="number"
                  value={formData.gstPercentage.sgst}
                  onChange={handleGstChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  error={!!errors.gstPercentage_sgst}
                  helperText={errors.gstPercentage_sgst}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="IGST"
                  name="igst"
                  type="number"
                  value={formData.gstPercentage.igst}
                  onChange={handleGstChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  error={!!errors.gstPercentage_igst}
                  helperText={errors.gstPercentage_igst}
                />
              </Grid>
            </Grid>

            {/* <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Visibility Settings
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isVisibleToEveryOne}
                    onChange={handleChange}
                    name="isVisibleToEveryOne"
                  />
                }
                label="Visible to Everyone"
              />
              <FormHelperText>
                If turned off, the plan will only be visible to specific employers
              </FormHelperText>
            </FormGroup> */}

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Visibility Settings
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isVisibleToEveryOne}
                    onChange={handleChange}
                    name="isVisibleToEveryOne"
                  />
                }
                label="Visible to Everyone"
              />
              <FormHelperText>
                If turned off, the plan will only be visible to specific employers
              </FormHelperText>
            </FormGroup>

            {!formData.isVisibleToEveryOne && (
              <Box sx={{ mt: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Select Employers
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        employerIds: [...formData.employerIds, ""]
                      });
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Typography>

                {formData.employerIds.length > 0 ? (
                  formData.employerIds.map((id, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`Employer ID ${index + 1}`}
                        value={id}
                        onChange={(e) => {
                          const newEmployerIds = [...formData.employerIds];
                          newEmployerIds[index] = e.target.value;
                          setFormData({
                            ...formData,
                            employerIds: newEmployerIds
                          });
                        }}
                        sx={{ mr: 1 }}
                      />
                      <IconButton
                        color="error"
                        onClick={() => {
                          const newEmployerIds = formData.employerIds.filter((_, i) => i !== index);
                          setFormData({
                            ...formData,
                            employerIds: newEmployerIds
                          });
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No employers added. Click the + button to add employers.
                  </Typography>
                )}
              </Box>
            )}

            {/* <FormGroup sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isBetaVersion}
                    onChange={handleChange}
                    name="isBetaVersion"
                  />
                }
                label="Beta Version"
              />
              <FormHelperText>
                Mark this plan as a beta version
              </FormHelperText>
            </FormGroup> */}
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 4 }}>
        <Button variant="outlined" color="inherit">

          <Link to="/plans" style={{ textDecoration: 'none', color: 'inherit' }}>
            <Typography variant="body2"> Cancel</Typography>
          </Link>

        </Button>
        <Button variant="contained" color="primary" type="submit">
          {isEditMode ? 'Update Plan' : (activeTab === 3 ? "Create Plan" : "Next")}
        </Button>
      </Box>
    </Box>
  );
};

export default PlanForm;